import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  AsyncValidator,
  ControlValueAccessor,
  UntypedFormControl,
  UntypedFormGroup,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  UntypedFormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TypeSelection } from '@components/type-selector/1.0';
import { Tenant } from '@generated/tcea/models/tenant';
import {
  ImageUploadId,
  ImageUploadValidationFactory,
  nonChannelImageIds,
} from '@helpers/image-upload-validation-factory';
import { BrandService } from '@services/brand/1.0/brand.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { combineLatest, firstValueFrom, Observable, of, Subject, timer } from 'rxjs';
import { catalogImageCvaValidate } from '@helpers/catalog-image-cva-validate';
import { filter, map, startWith, takeUntil } from 'rxjs/operators';
import {
  ChangeSetItemType,
  FormattedChangesForItem,
  ItemChanges,
} from '@interfaces/change-sets/1.0';
import { ButtonStyle } from '@constants/button/1.0';
import { CatalogImagesService } from '@services/catalog-images/1.0/catalog-images.service';
import { CatalogImageData } from '@interfaces/catalog/1.0/catalog-image';
import { BaseImageService } from '@services/base-image/1.0/base-image.service';
import { LoadingColor, LoadingType } from '@constants/loading/1.0';
import { CatalogItemsService } from '@services/catalog-items/1.0/catalog-items.service';
import { DeepCloneService } from '@services/utilities/1.0/deep-clone.service';
import { OrderingChannels } from '@interfaces/aggregator/1.0';
import { NgIf, NgClass, NgFor, AsyncPipe, KeyValuePipe } from '@angular/common';
import { TypeSelectorComponent } from '../type-selector/1.0/type-selector.component';
import { ChangeSetNoticeComponent } from '../change-set-notice/change-set-notice.component';
import { FileUploadComponent } from '../file-upload/1.0/file-upload.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { LoadingComponent } from '../loading/1.0/loading.component';
import { ButtonComponent } from '../button/1.0/button.component';
import { MatError } from '@angular/material/form-field';
@Component({
  selector: 'txp-catalog-item-images-base',
  templateUrl: './catalog-item-images-base.component.html',
  styleUrls: ['./catalog-item-images-base.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CatalogItemImagesBaseComponent),
      multi: true,
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => CatalogItemImagesBaseComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    NgClass,
    TypeSelectorComponent,
    NgFor,
    ChangeSetNoticeComponent,
    FileUploadComponent,
    MatCheckbox,
    LoadingComponent,
    ButtonComponent,
    MatError,
    TranslateModule,
    AsyncPipe,
    KeyValuePipe,
  ],
})
export class CatalogItemImagesBaseComponent
  implements ControlValueAccessor, OnInit, OnDestroy, AsyncValidator
{
  @Input() itemChanges: ItemChanges;
  @Input() selectedVersion: ChangeSetItemType;
  @Input() preventSelector: boolean;
  @Input() preventInfoTitle: boolean;
  @Input() imageUploadIds: ImageUploadId[]; // will display image upload option if provided

  buttonStyle = ButtonStyle;
  loadingType = LoadingType;
  loadingColor = LoadingColor;
  brand: Tenant;
  form: UntypedFormGroup;
  changeFn: any;
  touchedFn: any;
  selectedImage: CatalogImageData;
  imageTypes: TypeSelection[];
  ignoreChangeEvent: boolean;
  imageUploadId = ImageUploadId;
  isResizing = false;
  channelsOrder = () => 0;
  isReadOnly$: Observable<boolean> = of(false);
  orderingChannels = Object.values(OrderingChannels);
  validationPending$: Observable<boolean>;

  private unsubscribe = new Subject<void>();

  constructor(
    private brandService: BrandService,
    private translateService: TranslateService,
    private formBuilder: UntypedFormBuilder,
    private catalogImagesService: CatalogImagesService,
    private baseImageService: BaseImageService,
    private catalogItemsService: CatalogItemsService,
  ) {
    this.form = new UntypedFormGroup({});
    this.isReadOnly$ = this.catalogItemsService.isReadOnlyItem$;
    this.validationPending$ = combineLatest([
      this.baseImageService.validationPending$,
      this.form.statusChanges.pipe(
        startWith(this.form.status),
        map((status) => status === 'PENDING'),
      ),
    ]).pipe(map(([servicePending, formPending]) => servicePending || formPending));
  }

  ngOnInit() {
    this.baseImageService.resetMappings();
    this.brandService.currentBrandBehaviorSubject
      .pipe(
        takeUntil(this.unsubscribe),
        filter((brand) => brand.isSet),
      )
      .subscribe(async (brand) => {
        if (this.imageUploadIds) {
          this.brand = brand;
          this.setupImages(this.imageUploadIds);
          this.updateImageTypesHasValue();
        }
      });

    this.isReadOnly$.pipe(takeUntil(this.unsubscribe)).subscribe((isReadOnly) => {
      if (isReadOnly) {
        this.form.disable({ emitEvent: false });
      } else {
        this.form.enable({ emitEvent: false });
      }
    });

    // Subscribe to form value changes to update hasValue indicators
    this.form.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.updateImageTypesHasValue();
    });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  registerOnChange(fn: any): void {
    this.changeFn = fn;
    this.form.valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        filter(() => !this.ignoreChangeEvent),
      )
      .subscribe((value) => {
        if (this.selectedImage.id === ImageUploadId.BASE_IMAGE) {
          if (
            this.baseImageService.isBaseImageConfigurationStarted ||
            this.baseImageService.isBaseImageConfigurationFinished
          ) {
            if (this.baseImageService.isBaseImageConfigurationStarted) {
              this.setupCurrentBaseChannelConfiguration();
              return;
            }
          } else {
            this.setupBaseChannels();
          }
        }

        fn(value);
      });
  }

  registerOnTouched(fn: any): void {
    this.touchedFn = fn;
  }

  hasImageChanged(
    formForControl: AbstractControl,
    decoratorKey: string,
    versionToCompare: string,
    itemChanges: FormattedChangesForItem,
  ): boolean {
    const { decorators, images } = itemChanges || {};
    const sameVersion = versionToCompare === this.selectedVersion;

    return (
      !!decorators?.find((decorator) => {
        return decorator.field === 'images' && decorator.key === decoratorKey;
      }) ||
      images?.[decoratorKey] ||
      (sameVersion && formForControl?.dirty)
    );
  }

  getChangeSetImageKeys(): string[] {
    return Object.keys(this.form.controls || {});
  }

  getChangeSetImageControl(imageKey: string): AbstractControl | null {
    return imageKey ? this.form.get(imageKey) : null;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.form.disable({ emitEvent: false }) : this.form.enable({ emitEvent: false });
  }

  validate(control: AbstractControl): Observable<ValidationErrors | null> {
    return catalogImageCvaValidate(
      this.form,
      this.unsubscribe,
      this.translateService.instant('form.error.fieldsAreInvalid'),
    );
  }

  writeValue(data: any): void {
    this.form.reset(undefined, { emitEvent: false });
    this.resetBaseImageConfigurations();
    if (data) {
      this.form.patchValue(DeepCloneService.clone(data), { emitEvent: false });
      const baseImageValue = this.form.get(ImageUploadId.BASE_IMAGE)?.value;
      if (this.selectedImage?.id === ImageUploadId.BASE_IMAGE && baseImageValue) {
        if (baseImageValue instanceof File) {
          this.baseImageService.baseImageFile = baseImageValue;
          this.setupBaseChannels();
        } else if (typeof baseImageValue === 'string') {
          this.baseImageService.baseImageFile = undefined;
          this.catalogImagesService
            .fetchImageAsFile(baseImageValue, 'baseImage')
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
              next: (file) => {
                this.baseImageService.baseImageFile = file;
                this.setupBaseChannels();
              },
              error: () => {
                this.baseImageService.clearChannelPending();
              },
            });
        }
      }
    }

    // Update hasValue indicators after writing values
    this.updateImageTypesHasValue();

    // workaround for using async validator that never resets status past PENDING on initialization
    // need to manually trigger it
    setTimeout(() => {
      // we don't want to trigger the onChange function because it will mark the control dirty
      this.ignoreChangeEvent = true;
      this.form.updateValueAndValidity();
      this.ignoreChangeEvent = false;
    });
  }

  onSelectImageType(data: CatalogImageData) {
    this.selectedImage = data;
    if (data.id === ImageUploadId.BASE_IMAGE) {
      this.setupBaseChannels();
    }
  }

  setupImages(imageUploadIds: ImageUploadId[]) {
    const aggregators = this.brand?.aggregatorConfigurations || {};
    this.imageTypes = [];

    const shouldSkipImageChannel = (id) => {
      // Check if this is a non-channel image (logo, large, small, etc.)
      if (nonChannelImageIds.includes(id)) {
        return false;
      }

      return !aggregators[id]?.enabled;
    };

    for (let i = 0, len = imageUploadIds.length; i < len; i++) {
      const id = imageUploadIds[i];

      if (shouldSkipImageChannel(id)) {
        continue;
      }

      // Add the default image type (including consumer-api)
      const imageValidation = ImageUploadValidationFactory.get(id);
      this.imageTypes.push({
        label: this.translateService.instant('orders.grid.filters.orderSource.' + id),
        data: {
          id,
          allowedMimeTypes: imageValidation.allowedMimeTypes,
          instructions: imageValidation.instructions,
        },
      });
      const ctrl = new UntypedFormControl(
        undefined,
        imageValidation.validator,
        imageValidation.asyncValidator,
      );
      this.form.addControl(id, ctrl);

      // If consumer-api, add the ordering channels immediately after
      if (id === ImageUploadId.CONSUMER_API) {
        const consumerApiConfig = aggregators['consumer-api'];
        const orderingChannels = consumerApiConfig?.orderingChannels || {};

        Object.entries(orderingChannels).forEach(([channel, config]) => {
          if ((config as { enabled: boolean }).enabled) {
            const imageValidation = ImageUploadValidationFactory.get(ImageUploadId.CONSUMER_API);
            this.imageTypes.push({
              label: this.translateService.instant('orders.grid.filters.orderSource.' + channel),
              data: {
                id: channel,
                allowedMimeTypes: imageValidation.allowedMimeTypes,
                instructions: imageValidation.instructions,
              },
            });
            const ctrl = new UntypedFormControl(
              undefined,
              imageValidation.validator,
              imageValidation.asyncValidator,
            );
            this.form.addControl(channel, ctrl);
          }
        });
      }
    }

    if (this.imageTypes[0]) {
      this.selectedImage = this.imageTypes[0].data;
    }

    if (this.selectedImage.id === ImageUploadId.BASE_IMAGE) {
      const channels = this.imageTypes.filter((c) => {
        return c.data.id !== ImageUploadId.BASE_IMAGE;
      });
      channels.forEach((channel) => {
        // Use CONSUMER_API validation for ordering channels, otherwise use the channel's own validation
        const validationId = this.orderingChannels.includes(channel.data.id)
          ? ImageUploadId.CONSUMER_API
          : (channel.data.id as ImageUploadId);
        const imageChannelDefinition = ImageUploadValidationFactory.get(validationId);
        this.baseImageService.baseChannelsMapping[channel.data.id] = {
          pending: true,
          instructions: imageChannelDefinition.instructions,
          selected: false,
          validationId: validationId,
        };
      });
      this.baseImageService.syncValidationPending();
    }
  }

  getBaseChannelsMapping() {
    return this.baseImageService.baseChannelsMapping;
  }

  getBaseChannelsConfigurationMapping() {
    return this.baseImageService.baseChannelsConfigurationMapping;
  }

  isAnyChannelPending() {
    return this.baseImageService.isAnyChannelPending;
  }

  setupBaseChannels() {
    let baseImageControl: AbstractControl;
    const imageControl = this.form.get(ImageUploadId.BASE_IMAGE);
    const channels = this.baseImageService.baseChannelsMapping;

    if (imageControl && channels) {
      // Validations only work if we are passing a File object not an URL
      if (typeof imageControl.value === 'string') {
        baseImageControl = new FormControl(this.baseImageService.baseImageFile);
      } else {
        baseImageControl = imageControl;
      }

      Object.keys(channels).forEach((channelId) => {
        this.baseImageService.validateChannel(baseImageControl, channelId);
      });
    }
  }

  setupCurrentBaseChannelConfiguration() {
    const currentChannelBaseConfigChannelId = Object.keys(
      this.baseImageService.baseChannelsConfigurationMapping,
    ).find((channelId) => {
      return this.baseImageService.baseChannelsConfigurationMapping[channelId].current;
    });
    const currentChannelBaseConfig =
      this.baseImageService.baseChannelsConfigurationMapping[currentChannelBaseConfigChannelId];
    const imageControl = this.form.get(currentChannelBaseConfig.controlName);
    this.baseImageService.validateChannel(imageControl, currentChannelBaseConfigChannelId);
  }

  hasSelectedBaseImageChannels() {
    return this.baseImageService.hasSelectedBaseImageChannels();
  }

  isBaseImageConfigurationFinished() {
    return this.baseImageService.isBaseImageConfigurationFinished;
  }

  isBaseImageConfigurationStarted() {
    return this.baseImageService.isBaseImageConfigurationStarted;
  }

  async onBaseImageConfigurationStart() {
    const channelsToIterate = Object.keys(this.baseImageService.baseChannelsMapping).filter(
      (channelId) => {
        return (
          this.baseImageService.baseChannelsMapping[channelId].selected &&
          !this.baseImageService.baseChannelsMapping[channelId].valid
        );
      },
    );

    if (!channelsToIterate.length) {
      const selectedChannels = Object.keys(this.baseImageService.baseChannelsMapping).filter(
        (channelId) => {
          return this.baseImageService.baseChannelsMapping[channelId].selected;
        },
      );
      const baseImageValue = this.form.get(ImageUploadId.BASE_IMAGE)?.value;
      if (baseImageValue) {
        Object.keys(this.form.controls).forEach((channelId) => {
          if (selectedChannels.includes(channelId)) {
            this.form.controls[channelId].setValue(baseImageValue);
            this.form.controls[channelId].markAsDirty();
          }
        });
      }

      // Wait for validations to finish before completing the step
      if (this.form.pending) {
        await Promise.race([
          firstValueFrom(this.form.statusChanges.pipe(filter((s) => s !== 'PENDING'))),
          firstValueFrom(timer(5000)),
        ]);
      }

      this.baseImageService.isBaseImageConfigurationFinished = true;
      // Reset selected properties to enable save button
      selectedChannels.forEach((channelId) => {
        this.baseImageService.baseChannelsMapping[channelId].selected = false;
      });
    } else {
      channelsToIterate.forEach((channelId, index) => {
        const imageType = this.imageTypes.find((i) => i.data.id === channelId);
        const file = this.form.get(ImageUploadId.BASE_IMAGE)?.value;
        const controlName = `base-image-setting-${channelId}`;
        this.form.addControl(controlName, new UntypedFormControl(file), { emitEvent: false });
        this.baseImageService.baseChannelsConfigurationMapping[channelId] = {
          controlName: controlName,
          current: false,
          allowedMimeTypes: imageType.data.allowedMimeTypes,
        };
        if (index === 0) {
          this.baseImageService.baseChannelsConfigurationMapping[channelId].current = true;
        }
      });
      this.baseImageService.isBaseImageConfigurationStarted = true;
    }
  }

  onSelectBaseImageChannel($event: any, channelId: string) {
    this.baseImageService.baseChannelsMapping[channelId].selected = $event.checked;

    if ($event.checked && this.baseImageService.baseChannelsMapping[channelId].valid) {
      const baseImageValue = this.form.get(ImageUploadId.BASE_IMAGE)?.value;
      if (baseImageValue) {
        const ctrl = this.form.get(channelId);
        ctrl?.setValue(baseImageValue, { emitEvent: false });
        ctrl?.markAsDirty();
      }
    } else if (!$event.checked) {
      // If deselected, we should probably clear it if it was populated from base image
      const currentVal = this.form.get(channelId)?.value;
      const baseImageValue = this.form.get(ImageUploadId.BASE_IMAGE)?.value;
      if (currentVal === baseImageValue) {
        const ctrl = this.form.get(channelId);
        ctrl?.setValue(undefined, { emitEvent: false });
        ctrl?.markAsPristine();
      }
    }
  }

  onBaseImageConfigurationNavigation(currentIndex: number, back = false) {
    const channels = Object.keys(this.baseImageService.baseChannelsConfigurationMapping);
    if (currentIndex !== -1) {
      this.baseImageService.baseChannelsConfigurationMapping[channels[currentIndex]].current =
        false;
      // if next key is not undefined, set it to current
      if (channels[currentIndex + (back ? -1 : 1)]) {
        this.baseImageService.baseChannelsConfigurationMapping[
          channels[currentIndex + (back ? -1 : 1)]
        ].current = true;
      } else if (currentIndex === 0 && back) {
        this.baseImageService.isBaseImageConfigurationStarted = false;
        this.removeBaselChannelConfigurationControls();
        this.baseImageService.baseChannelsConfigurationMapping = {};
        this.setupBaseChannels();
      } else {
        this.updateFormChannelsFromConfigured();
      }
    }
  }

  updateFormChannelsFromConfigured() {
    this.baseImageService.isBaseImageConfigurationFinished = true;
    this.baseImageService.isBaseImageConfigurationStarted = false;
    const selectedChannels = Object.keys(this.baseImageService.baseChannelsMapping).filter(
      (channelId) => {
        return (
          this.baseImageService.baseChannelsMapping[channelId].selected &&
          this.baseImageService.baseChannelsMapping[channelId].valid
        );
      },
    );
    selectedChannels.forEach((channelId) => {
      let baseImage: any;
      if (this.baseImageService.baseChannelsConfigurationMapping[channelId]) {
        baseImage = this.form.get(
          this.baseImageService.baseChannelsConfigurationMapping[channelId].controlName,
        ).value;
      } else {
        baseImage = this.form.get(ImageUploadId.BASE_IMAGE)?.value;
      }

      this.form.controls[channelId].setValue(baseImage);
      this.form.controls[channelId].markAsDirty(); // required to update change set
      this.baseImageService.baseChannelsMapping[channelId].selected = false;
    });
  }

  isBaseNavigationNextButtonDisabled(currentIndex: number) {
    return this.baseImageService.isBaseNavigationNextButtonDisabled(currentIndex);
  }

  /**
   * A saved URL is not usable until it has been downloaded. A failed download
   * leaves valid unset, and the last crop step would then disable Next with no message.
   */
  isBaseChannelNextDisabled(): boolean {
    const value = this.form.get(ImageUploadId.BASE_IMAGE)?.value;
    return typeof value === 'string' && !this.baseImageService.baseImageFile;
  }

  resetBaseImageConfigurations() {
    this.baseImageService.isBaseImageConfigurationFinished = false;
    this.baseImageService.isBaseImageConfigurationStarted = false;
    this.removeBaselChannelConfigurationControls();
    this.baseImageService.baseChannelsConfigurationMapping = {};
    if (this.baseImageService.baseChannelsMapping) {
      Object.keys(this.baseImageService.baseChannelsMapping).forEach((key) => {
        this.baseImageService.baseChannelsMapping[key].selected = false;
      });
    }
  }

  removeBaselChannelConfigurationControls() {
    if (this.baseImageService.baseChannelsConfigurationMapping) {
      Object.keys(this.baseImageService.baseChannelsConfigurationMapping).forEach((key) => {
        this.form.removeControl(
          this.baseImageService.baseChannelsConfigurationMapping[key].controlName,
          {
            emitEvent: false,
          },
        );
      });
    }
  }

  updateIsResizing(isResizing: boolean) {
    this.isResizing = isResizing;
  }

  /**
   * Updates the hasValue property on each imageType based on form control values.
   * This enables the type-selector to show checkmarks for channels with images.
   */
  updateImageTypesHasValue(): void {
    if (!this.imageTypes) {
      return;
    }

    this.imageTypes.forEach((imageType) => {
      const control = this.form.get(imageType.data.id);
      imageType.hasValue = !!control?.value;
    });
  }
}
